import { apiService } from './api';
import { User, SignupData } from '../types';
import { withRetry } from '../utils/retryUtils';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  session?: any;
  message: string;
}

export const authService = {
  // Login user
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await apiService.post<AuthResponse>('/api/v1/auth/login', credentials);
    return response;
  },

  // Signup user
  signup: async (userData: SignupData): Promise<AuthResponse> => {
    const response = await apiService.post<AuthResponse>('/api/v1/auth/signup', userData);
    return response;
  },

  // Quick signup user (Step 1 only)
  quickSignup: async (userData: { name: string; email: string; password: string }): Promise<AuthResponse> => {
    return withRetry(async () => {
      const response = await apiService.post<AuthResponse>('/api/v1/auth/signup/quick', userData);
      return response;
    }, {
      maxAttempts: 2, // Reduce attempts for rate limiting
      baseDelay: 2000, // Increase delay for rate limiting
      retryCondition: (error) => {
        // Don't retry on validation errors (4xx) except rate limiting
        const status = error.response?.status;
        return !status || status >= 500 || status === 429;
      }
    });
  },

  // Logout user
  logout: async (): Promise<void> => {
    await apiService.post<void>('/api/v1/auth/logout');
  },

  // Forgot password
  forgotPassword: async (email: string): Promise<void> => {
    await apiService.post<void>('/api/v1/auth/forgot-password', { email });
  },

  // Reset password
  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await apiService.post<void>('/api/v1/auth/reset-password', { token, new_password: newPassword });
  },

  // Get current user
  getCurrentUser: async (): Promise<User> => {
    const response = await apiService.getCurrentUser();
    return response;
  },

  // Update user profile
  updateProfile: async (userData: Partial<User>): Promise<User> => {
    const response = await apiService.put<User>('/api/v1/auth/profile', userData);
    return response;
  },

  // Change password
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiService.post<void>('/api/v1/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  // Verify email
  verifyEmail: async (token: string): Promise<{ user_id: string }> => {
    return await apiService.post<{ user_id: string }>('/api/v1/auth/verify-email', { token });
  },

  // Resend verification email
  resendVerification: async (): Promise<void> => {
    return withRetry(async () => {
      await apiService.post<void>('/api/v1/auth/resend-verification');
    }, {
      maxAttempts: 2,
      baseDelay: 2000,
      retryCondition: (error) => {
        const status = error.response?.status;
        return !status || status >= 500 || status === 429;
      }
    });
  },

  // Refresh token
  refreshToken: async (): Promise<{ token: string }> => {
    const response = await apiService.post<{ token: string }>('/api/v1/auth/refresh');
    return response;
  },

  // Change email
  changeEmail: async (newEmail: string): Promise<{ message: string; status: string; new_email: string }> => {
    const response = await apiService.post<{ message: string; status: string; new_email: string }>('/api/v1/auth/change-email', {
      new_email: newEmail,
    });
    return response;
  },

  // Verify email change
  verifyEmailChange: async (token: string): Promise<void> => {
    await apiService.post<void>('/api/v1/auth/verify-email-change', { token });
  },

  // Complete email change (for stuck pending states)
  completeEmailChange: async (): Promise<{ message: string; new_email: string }> => {
    const response = await apiService.post<{ message: string; new_email: string }>('/api/v1/auth/complete-email-change');
    return response;
  },

  // Update signup progress
  updateSignupProgress: async (step: number, data: any): Promise<any> => {
    const response = await apiService.post<any>('/api/v1/auth/signup/progress', {
      step,
      data
    });
    return response;
  },

  // Get signup progress
  getSignupProgress: async (email: string): Promise<any> => {
    const response = await apiService.get<any>(`/api/v1/auth/signup/progress/${email}`);
    return response;
  },
};

export default authService; 